import AddIcon from "@mui/icons-material/Add";
import { Box, Checkbox, Divider, Stack, TextField } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import { Controller } from "react-hook-form";

import { useSession } from "../Auth/api/useSession";
import { useIsAdmin } from "../ExperimentalHomeHealth/lib/useIsAdmin";
import { Button } from "../redesign/components/Button";
import { DialogFooter } from "../redesign/components/DialogFooter";
import { ResponsiveDialog } from "../redesign/components/ResponsiveDialog";
import { Text } from "../redesign/components/Text";
import { type WorkerInvitationOpenedFrom } from "./types";
import { useWorkerInvitationForm } from "./useWorkerInvitationForm";
import { sendWorkerInvitationDialogActionedEvent } from "./utils";

interface Props {
  openedFrom: WorkerInvitationOpenedFrom;
  modalState: UseModalState;
  isPrnWorkersInvitationRolloutEnabled?: boolean;
  onInvitationSuccess?: () => void;
}

export function WorkerInvitationDialog({
  openedFrom,
  modalState,
  isPrnWorkersInvitationRolloutEnabled = false,
  onInvitationSuccess,
}: Props) {
  const {
    profile: { userId: workplaceId },
  } = useSession();
  const isAdmin = useIsAdmin();

  const {
    control,
    fields,
    dialogContentRef,
    shouldShowFieldError,
    hasAtLeastOneValidRow,
    addNewStaffMember,
    resetForm,
    handleSubmit,
    isSendingInvitations,
  } = useWorkerInvitationForm({
    openedFrom,
    isAdmin,
    workplaceId,
    onSuccess: () => {
      resetForm();
      onInvitationSuccess?.();
      modalState.closeModal();
    },
  });

  const handleClose = () => {
    sendWorkerInvitationDialogActionedEvent({
      workplaceId,
      openedFrom,
      action: "CANCEL",
      isAdmin,
    });
    resetForm();
    modalState.closeModal();
  };

  const columnWidth = isPrnWorkersInvitationRolloutEnabled ? "45%" : "50%";

  return (
    <ResponsiveDialog
      modalState={modalState}
      title="Invite Staff to Pick Up Shifts on Clipboard"
      width={700}
      footerContent={
        <DialogFooter
          secondaryAction={{
            label: "Cancel",
            variant: "outlined",
            onClick: handleClose,
          }}
          primaryAction={{
            label: "Send Invitations",
            variant: "primary",
            disabled: !hasAtLeastOneValidRow() || isSendingInvitations,
            isLoading: isSendingInvitations,
            onClick: () => {
              void handleSubmit();
            },
          }}
        />
      }
      onClose={handleClose}
    >
      <Stack spacing={3}>
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Add staff members you like working with to join Clipboard and pick up your shifts
        </Text>

        {/* Column headers */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Text bold variant="body2" sx={{ width: columnWidth }}>
            Name
          </Text>
          <Text bold variant="body2" sx={{ width: columnWidth }}>
            Phone Number
          </Text>
          {isPrnWorkersInvitationRolloutEnabled && (
            <Text bold variant="body2" sx={{ width: "10%" }}>
              Is PRN worker?
            </Text>
          )}
        </Box>

        {/* Scrollable form rows */}
        <Box
          ref={dialogContentRef}
          sx={{
            height: "250px",
            overflowY: "auto",
          }}
        >
          {fields.map((field, index) => (
            <Box key={field.id} sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
              <Controller
                name={`staffMembers.${index}.name`}
                control={control}
                render={({ field: nameField }) => (
                  <TextField
                    {...nameField}
                    fullWidth
                    size="small"
                    placeholder="Enter full name"
                    error={shouldShowFieldError(index, "name")}
                    inputProps={{
                      "aria-label": `Name field for row ${index + 1}`,
                    }}
                    sx={{ width: columnWidth }}
                    onChange={(event) => {
                      nameField.onChange(event.target.value);
                    }}
                  />
                )}
              />
              <Controller
                name={`staffMembers.${index}.phone`}
                control={control}
                render={({ field: phoneField }) => (
                  <TextField
                    {...phoneField}
                    fullWidth
                    size="small"
                    placeholder="(555) 123-4567"
                    inputProps={{
                      "aria-label": `Phone field for row ${index + 1}`,
                    }}
                    error={shouldShowFieldError(index, "phone")}
                    sx={{ width: columnWidth }}
                    onChange={(event) => {
                      const value = event.target.value.replaceAll(/\D/g, "");
                      if (value.length <= 10) {
                        phoneField.onChange(value);
                      }
                    }}
                  />
                )}
              />
              {isPrnWorkersInvitationRolloutEnabled && (
                <Controller
                  name={`staffMembers.${index}.isPrnWorker`}
                  control={control}
                  render={({ field: isPrnWorkerField }) => (
                    <Checkbox
                      checked={Boolean(isPrnWorkerField.value)}
                      inputProps={{
                        "aria-label": `Is PRN worker field for row ${index + 1}`,
                      }}
                      sx={{ width: "10%" }}
                      onChange={(event) => {
                        isPrnWorkerField.onChange(event.target.checked);
                      }}
                    />
                  )}
                />
              )}
            </Box>
          ))}

          <Button
            variant="link"
            sx={(theme) => ({ marginTop: theme.spacing(1) })}
            onClick={addNewStaffMember}
          >
            <AddIcon sx={{ fontSize: "1.25rem", marginRight: 0.5 }} />
            Add more staff
          </Button>
        </Box>

        <Divider />
      </Stack>
    </ResponsiveDialog>
  );
}
